import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { WINDOW_CHOICES, type ListView } from '../lib/listView.js';

/**
 * The toolbar every list wears.
 *
 * Built once and adopted rather than reimplemented per screen, because the failure this exists
 * to prevent is not "the filters look different" — it is a coordinator staring at an empty table
 * with no idea a filter is on. That happens when the state of a filter is only visible inside a
 * dropdown nobody has open.
 *
 * So the rule here: **anything that is narrowing the list says so on screen, as a chip you can
 * click off.** The period included, even when it came from a preference rather than a click —
 * especially then, because that is the one nobody remembers setting.
 *
 * Dense on purpose. This is a desktop tool somebody has open for eight hours, not a landing
 * page: one row of controls at 34px, a second row only when there is something to say.
 */

/** One filter in force, and how to let go of it. */
export type FilterChip = {
  id: string;
  /** What is filtered, in the user's language: "Statut : Payée". */
  label: string;
  onRemove: () => void;
};

/**
 * The row cap, mirrored from `EXPORT_MAX_ROWS` in the API so the warning can be given *before*
 * the download rather than after. The server stays the authority — it says what it actually did
 * in `x-export-truncated` — and this is only ever used to say "this is going to be short".
 */
export const EXPORT_LIMIT = 10_000;

type Props = {
  view: ListView;
  /** Placeholder for the text filter. Absent hides the search box entirely. */
  searchPlaceholder?: string;
  /** The screen's own controls — a status select, a toggle — sitting inline with the rest. */
  children?: ReactNode;
  /** Every filter in force other than the period, which the toolbar owns. */
  chips?: FilterChip[];
  /** Clears the screen's own filters. The toolbar clears the ones it owns. */
  onClearAll?: () => void;
  /** Which date columns this endpoint understands. One or none hides the choice. */
  dateFields?: Array<{ value: string; label: string }>;
  /**
   * Whether to offer a period at all.
   *
   * Distinct from `useListView`'s `timeBased`, which decides whether the saved default *applies*:
   * the customer directory offers the period and never starts with one.
   */
  showPeriod?: boolean;
  /** Rows matching the filters, so the export can warn before it truncates. */
  total?: number;
  onExport?: () => void;
  exporting?: boolean;
  /** The outcome of the last export, already worded. Cleared by the screen. */
  exportNote?: string | null;
  exportFailed?: boolean;
};

export function ListToolbar({
  view,
  searchPlaceholder,
  children,
  chips = [],
  onClearAll,
  dateFields,
  showPeriod = true,
  total,
  onExport,
  exporting,
  exportNote,
  exportFailed,
}: Props) {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;

  const periodChip: FilterChip | null =
    showPeriod && view.window !== 'all'
      ? {
          id: 'period',
          label: `${t('list.period')} : ${windowLabel(view, t)}`,
          // Removing the period means "everything", explicitly — which also stops the saved
          // default applying to this link, so a shared URL keeps showing everything.
          onRemove: () => view.setWindow('all'),
        }
      : null;

  const searchChip: FilterChip | null = view.q
    ? { id: 'q', label: `${t('list.search')} : ${view.q}`, onRemove: () => view.set({ q: undefined }) }
    : null;

  const allChips = [periodChip, searchChip, ...chips].filter((c): c is FilterChip => c !== null);
  const willTruncate = total !== undefined && total > EXPORT_LIMIT;

  return (
    <div className="listbar">
      <div className="listbar__row">
        {searchPlaceholder !== undefined && (
          <label className="listbar__search">
            <span className="sr-only">{t('list.search')}</span>
            <input
              type="search"
              // The value comes from the URL, so this input holds no state of its own and a
              // reload restores exactly what was typed.
              value={view.q}
              onChange={(e) => view.set({ q: e.target.value || undefined })}
              placeholder={searchPlaceholder}
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
            />
          </label>
        )}

        {children}

        {showPeriod && <PeriodControl view={view} dateFields={dateFields} t={t} />}

        {onExport && (
          <button
            type="button"
            className="listbar__export"
            onClick={onExport}
            disabled={exporting || total === 0}
            title={willTruncate ? t('list.exportCapped', { total, limit: EXPORT_LIMIT }) : undefined}
          >
            <DownloadGlyph />
            {exporting ? t('list.exporting') : t('list.export')}
            {/* Said before the click, not discovered afterwards by counting rows. */}
            {willTruncate && <span className="listbar__warn" aria-hidden="true">!</span>}
          </button>
        )}
      </div>

      {/*
        * The chips. The container is always in the document, even with nothing in it: an empty
        * flex row is zero pixels tall, and a live region has to exist before its contents change
        * or the announcement never happens.
        */}
      <div className="listbar__chips" role="status" aria-label={t('list.activeFilters')}>
        {allChips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className="listbar__chip"
            onClick={chip.onRemove}
            aria-label={t('list.remove', { label: chip.label })}
          >
            <span>{chip.label}</span>
            <span className="listbar__chipX" aria-hidden="true">
              ×
            </span>
          </button>
        ))}

        {allChips.length > 1 && (
          <button
            type="button"
            className="listbar__clear"
            onClick={() => {
              onClearAll?.();
              view.set({ q: undefined, window: 'all', from: undefined, to: undefined });
            }}
          >
            {t('list.clearAll')}
          </button>
        )}
      </div>

      {willTruncate && (
        <p className="listbar__note listbar__note--warn">{t('list.exportCapped', { total, limit: EXPORT_LIMIT })}</p>
      )}
      {exportNote && (
        <p className={`listbar__note${exportFailed ? ' listbar__note--warn' : ''}`} role="status">
          {exportNote}
        </p>
      )}
    </div>
  );
}

/**
 * The period, as a button that opens a panel.
 *
 * A popover rather than two date inputs sitting in the toolbar, because nine times out of ten
 * the answer is one of five presets and typing two dates for "the last 30 days" is absurd. The
 * tenth time is what the custom pair is for.
 *
 * What makes it safe to hide behind a button: the chosen period is always also a chip, outside,
 * with the panel closed.
 */
function PeriodControl({
  view,
  dateFields,
  t,
}: {
  view: ListView;
  dateFields?: Array<{ value: string; label: string }>;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ from: string; to: string }>({ from: view.from ?? '', to: view.to ?? '' });
  const panelId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus goes back where it came from. Without this, escaping the panel drops focus on the
      // document and the next Tab starts from the top of the page.
      trigger.current?.focus();
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || trigger.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const alreadyDefault = view.window !== 'custom' && view.window === view.savedDefaultWindow;

  return (
    <div className="period">
      <button
        ref={trigger}
        type="button"
        className={`period__button${view.window !== 'all' ? ' period__button--on' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setDraft({ from: view.from ?? '', to: view.to ?? '' });
          setOpen((was) => !was);
        }}
      >
        <CalendarGlyph />
        <span className="period__label">{windowLabel(view, t)}</span>
        {/* Says out loud that this period was never chosen — it is the account's default. The
            one filter people forget is on is the one they never turned on. */}
        {view.windowIsDefault && view.window !== 'all' && <span className="period__auto">•</span>}
      </button>

      {open && (
        <div ref={panel} id={panelId} className="period__panel" role="dialog" aria-label={t('list.period')}>
          <div className="period__presets" role="group" aria-label={t('list.period')}>
            {WINDOW_CHOICES.map((choice) => (
              <button
                key={choice}
                type="button"
                className={`period__preset${view.window === choice ? ' period__preset--on' : ''}`}
                aria-pressed={view.window === choice}
                onClick={() => {
                  view.setWindow(choice);
                  setOpen(false);
                  trigger.current?.focus();
                }}
              >
                {t(`list.window.${choice}`)}
              </button>
            ))}
          </div>

          <div className="period__custom">
            <label className="period__field">
              <span className="label">{t('list.from')}</span>
              <input
                type="date"
                value={draft.from}
                max={draft.to || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
              />
            </label>
            <label className="period__field">
              <span className="label">{t('list.to')}</span>
              <input
                type="date"
                value={draft.to}
                min={draft.from || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
              />
            </label>
            <button
              type="button"
              className="period__apply"
              // An open-ended range is legitimate — "everything since March" — so only a pair
              // with neither end is refused.
              disabled={!draft.from && !draft.to}
              onClick={() => {
                view.setWindow('custom', draft.from || undefined, draft.to || undefined);
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              {t('list.apply')}
            </button>
          </div>

          {/* Which date the period is measured against. Absent where there is only one honest
              answer, because a select with one option is furniture. */}
          {dateFields && dateFields.length > 1 && (
            <label className="period__field period__field--wide">
              <span className="label">{t('list.dateField')}</span>
              <select value={view.dateField} onChange={(e) => view.set({ dateField: e.target.value })}>
                {dateFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* A specific pair of dates cannot be a default: it would be stale tomorrow. And a
              list the default never applies to must not offer to set it. */}
          {view.window !== 'custom' && view.appliesDefault && (
            <button
              type="button"
              className="period__default"
              disabled={alreadyDefault || view.savingDefault}
              onClick={() => view.saveWindowAsDefault()}
            >
              {alreadyDefault ? t('list.defaultSaved') : t('list.makeDefault')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The period in force, as the reader would say it.
 *
 * A custom range reads as its two dates, and an open-ended one as the end it has — "depuis le
 * 2026-03-01" would need a third string for a case nobody uses, so both ends are shown with an
 * en dash and the missing side left blank.
 */
function windowLabel(view: ListView, t: (key: string, vars?: Record<string, string | number>) => string): string {
  if (view.window !== 'custom') return t(`list.window.${view.window}`);
  const from = view.from ?? '';
  const to = view.to ?? '';
  return `${from} – ${to}`.trim();
}

/**
 * Said where the rows would be, not in the toolbar.
 *
 * An empty table under a period nobody chose is the single worst state this feature can produce,
 * and the fix is to put the explanation and the way out in the space where the missing rows
 * would have been.
 */
export function ListWindowNotice({
  view,
  outsideWindow,
  empty,
}: {
  view: ListView;
  /** Rows the period is holding back, counted by the API rather than guessed. */
  outsideWindow: number;
  /** True when the current page has nothing on it. */
  empty: boolean;
}) {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;

  if (view.window === 'all' || outsideWindow <= 0) return null;

  return (
    <div className={`windownotice${empty ? ' windownotice--empty' : ''}`} role="status">
      <span>
        {empty && `${t('list.windowEmpty')} `}
        {t('list.windowNotice', { count: outsideWindow })}
      </span>
      <button type="button" className="windownotice__action" onClick={() => view.setWindow('all')}>
        {t('list.showEverything')}
      </button>
    </div>
  );
}

function CalendarGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <rect x="1" y="2.5" width="10" height="8.5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.1" />
      <path d="M1 5h10M4 1v2.5M8 1v2.5" fill="none" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  );
}

function DownloadGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M6 1v7m0 0L3.5 5.5M6 8l2.5-2.5M1.5 10.5h9" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
